import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SlideDeckComparisonService } from '../../../core/data-services/company/slide-deck-comparison.service';

/**
 * The intrinsic pixel dimensions of a loaded slide image, reported by {@link RmsLazyBlobImageComponent#imageSize}
 * so a parent can size its own frame to the image's real aspect ratio instead of guessing one.
 */
export interface ImageSize {
  width: number;
  height: number;
}

/**
 * Reusable lazily-loaded slide image (UI-08 / D-09). Defers the authenticated blob fetch until the
 * element nears the viewport via an IntersectionObserver (so a report with hundreds of slides does
 * not fire hundreds of concurrent GETs on load), binds the fetched blob to an `<img>` through an
 * object URL, and strictly manages that object URL's lifetime — revoking it on destroy, before any
 * re-fetch, and (since RA-2706) when the element scrolls far enough out of view.
 *
 * It is the single component in the slide-deck-comparison feature with no in-repo analog, so the
 * observer/fetch/object-URL lifecycle is implemented carefully and proven by its spec.
 *
 * ⚠️ **Shared by location, not by use.** It lives under `shared/components/` and is declared and
 * exported by `SharedModule`, but its only consumers are `sdc-pair-card` and `sdc-evidence-slide` —
 * both inside the slide-deck-comparison feature. That is why it carries `OnPush`: it is instantiated
 * once per slide thumbnail, so hundreds of instances exist on one report page. A future consumer
 * outside this feature inherits those OnPush semantics — every state change here is already followed
 * by an explicit `markForCheck()`, but a new `@Input` mutated in place by a parent will not refresh.
 *
 * ## The two-margin lifecycle (RA-2706)
 * Pair cards are never removed from the DOM — the container hides filtered-out cards with `[hidden]`
 * so the rail can still scroll to them — so a full scroll through a 120-pair report would otherwise
 * leave ~240 image blobs pinned for the lifetime of the page. Two observers with deliberately
 * different margins give the hysteresis that makes releasing safe:
 *
 * - **load** at `rootMargin: 200px` — fetch as the element nears the viewport
 * - **release** at `rootMargin: 6000px` (roughly five to six viewport heights) — revoke once it is a
 *   long way away, and let the load observer re-fetch on re-entry
 *
 * 🔴 The two margins **must not be equal or close**. At a single threshold, ordinary scrolling sits
 * on the boundary and thrashes: revoke, immediately re-fetch, revoke again — strictly worse than
 * never releasing at all.
 *
 * ⚠️ **Distance is not the only way to stop intersecting.** A `display: none` element reports as
 * non-intersecting regardless of `rootMargin`, and this feature hides content that way routinely
 * (the Top-Down accordion body, and the status filter's `[hidden]` on pair cards). Releasing on
 * those would make a one-click interaction flash the loading placeholder across the report, so the
 * release observer additionally requires a non-zero bounding rect — see {@link isLaidOut}.
 *
 * ⚠️ **Why 6000px and not the ~2000px this shipped with first.** Releasing costs a visible artifact: a
 * released component returns to `'idle'`, and the template renders the "Loading image…" placeholder for
 * `idle` as well as `loading` — so scrolling back up flashes the placeholder before the bytes resolve.
 * The bytes themselves are cheap on re-entry (the response carries
 * `Cache-Control: private, max-age=86400` and the ref-keyed URL is stable, so the browser serves them
 * from its own HTTP cache — no network, no backend, no S3), but the flash is real. At 2000px an
 * average-sized report (~50 pairs) paid that flash without ever having been a memory problem in the
 * first place. 6000px keeps the bound where it matters — a ~120-pair report still cannot pin its whole
 * image set — while leaving ordinary up-and-down reading inside one report untouched.
 */
@Component({
  selector: 'rms-lazy-blob-image',
  templateUrl: './rms-lazy-blob-image.component.html',
  styleUrls: ['./rms-lazy-blob-image.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RmsLazyBlobImageComponent implements OnInit, OnDestroy {

  /** Distance from the viewport at which the blob is fetched. */
  private static readonly LOAD_MARGIN = '200px';

  /**
   * Distance from the viewport at which the blob is released. Must stay well above LOAD_MARGIN — see the
   * class doc for why this is 6000px rather than the ~2000px it shipped with.
   */
  private static readonly RELEASE_MARGIN = '6000px';

  @Input() comparisonId: number;
  @Input() slideRef: string;
  @Input() alt = '';

  /**
   * Emits the loaded image's intrinsic pixel dimensions so a parent can give its frame the slide's real
   * aspect ratio (RA-2256 — otherwise a non-16:9 slide is letterboxed inside a fixed-ratio frame and
   * every normalized 0..1 region box drawn over it is offset). Emitted once per actual image load.
   */
  @Output() imageSize = new EventEmitter<ImageSize>();

  public state: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  public objectUrl: string | null = null;

  /** Fires the fetch when the element nears the viewport. Never disconnected — re-entry re-fetches. */
  private loadObserver: IntersectionObserver;

  /** Releases the blob when the element moves well clear of the viewport. */
  private releaseObserver: IntersectionObserver;

  /** Emits when an in-flight fetch must be abandoned because the element was released. */
  private release$ = new Subject<void>();

  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private dataService: SlideDeckComparisonService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Registers the two IntersectionObservers (outside Angular's zone to avoid spurious change
   * detection) that drive the load/release lifecycle described on the class. With a null/empty
   * slideRef the component goes straight to the broken-image state and never observes or fetches —
   * the missing side of an ADDED/REMOVED pair is handled by the caller.
   *
   * 🔴 Both callbacks re-check the state BEFORE `ngZone.run`, duplicating the guard inside
   * {@link fetch} / {@link release}. That is not redundant: `NgZone.run` fires `onMicrotaskEmpty` on
   * exit, so it costs a full `ApplicationRef.tick()` whatever the callback does — and each of the
   * hundreds of instances on a report owns its own observer pair, so an unguarded zone entry is one
   * whole-page change-detection pass per image. The load observer is deliberately never disconnected
   * (a released element must be able to re-fetch), so without the outer guard every scroll back over
   * already-loaded content would tick once per thumbnail. The inner guards stay as the invariant.
   */
  public ngOnInit(): void {
    if (!this.slideRef) {
      this.state = 'error';
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (this.state === 'idle' && entries.some(e => e.isIntersecting)) {
            this.ngZone.run(() => this.fetch());
          }
        },
        { rootMargin: RmsLazyBlobImageComponent.LOAD_MARGIN }
      );
      this.loadObserver.observe(this.el.nativeElement);

      this.releaseObserver = new IntersectionObserver(
        (entries) => {
          if (this.hasReleasableBlob() && entries.some(e => !e.isIntersecting && this.isLaidOut(e))) {
            this.ngZone.run(() => this.release());
          }
        },
        { rootMargin: RmsLazyBlobImageComponent.RELEASE_MARGIN }
      );
      this.releaseObserver.observe(this.el.nativeElement);
    });
  }

  /**
   * Disconnects both observers and revokes the current object URL so no blob is pinned after the
   * element leaves the DOM (the critical leak-prevention path for hundreds of slides).
   */
  public ngOnDestroy(): void {
    this.loadObserver?.disconnect();
    this.releaseObserver?.disconnect();
    this.revoke();
    this.release$.next();
    this.release$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Reports the just-decoded image's intrinsic size to the parent (RA-2256).
   *
   * ⚠️ A `(load)` binding is zone-patched, so this does tick the app — but it fires once per actual
   * image load, immediately after the HTTP response that already ticked, so it adds no per-frame or
   * per-change-detection cost. Nothing here may move into the observer callbacks.
   *
   * A decode failure or a detached image reports `0 x 0`; emitting that would give the parent an
   * invalid `aspect-ratio` and collapse its frame, so a zero dimension is dropped and the parent keeps
   * its fallback. The error branch renders no `<img>` at all, so it never reaches this handler.
   *
   * @param {Event} event the image's `load` event
   */
  public onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    const width = img?.naturalWidth ?? 0;
    const height = img?.naturalHeight ?? 0;
    if (width > 0 && height > 0) {
      this.imageSize.emit({ width, height });
    }
  }

  /**
   * Fetches the slide image as an authenticated blob and binds it via an object URL on success, or
   * sets the broken-image state on failure. Any previously-bound object URL is revoked before the
   * fetch so a re-fetch cannot leak the prior URL.
   *
   * Guarded on `state === 'idle'` because the load observer is no longer disconnected after the
   * first hit: it keeps firing as the element crosses the load margin, and a released element goes
   * back to `'idle'` precisely so the next crossing re-fetches. A loaded, loading, or errored
   * element is left alone — an errored one deliberately does not retry on every scroll past.
   *
   * The subscription is torn down by either a release or a destroy, so a response that arrives after
   * the element was released cannot bind an object URL to a component that no longer wants one.
   */
  private fetch(): void {
    if (this.state !== 'idle') {
      return;
    }
    this.state = 'loading';
    this.revoke();
    this.cdr.markForCheck();
    this.dataService.getSlideImage(this.comparisonId, this.slideRef)
      .pipe(takeUntil(merge(this.release$, this.destroy$)))
      .subscribe(
        (blob) => {
          this.objectUrl = URL.createObjectURL(blob);
          this.state = 'loaded';
          this.cdr.markForCheck();
        },
        () => {
          this.state = 'error';
          this.cdr.markForCheck();
        }
      );
  }

  /**
   * Releases the blob when the element has moved well clear of the viewport: abandons any in-flight
   * fetch, revokes the object URL, and returns the component to `'idle'` so the load observer
   * re-fetches when the element comes back.
   *
   * A component that never loaded (still `'idle'`) or that failed (`'error'`) is left as it is —
   * there is nothing to release, and resetting an errored element would turn every scroll past it
   * into another failed request.
   */
  private release(): void {
    if (this.state === 'idle' || this.state === 'error') {
      return;
    }
    this.release$.next();
    this.revoke();
    this.state = 'idle';
    this.cdr.markForCheck();
  }

  /**
   * Whether there is anything for {@link release} to do — i.e. a blob is bound or a fetch is in
   * flight. Read by the release observer before it enters the zone, so an element that never loaded
   * (or failed) costs no change detection when it drifts past the release margin.
   * @returns {boolean} true when a release would actually change state
   */
  private hasReleasableBlob(): boolean {
    return this.state === 'loaded' || this.state === 'loading';
  }

  /**
   * Whether an observer entry describes an element that is still laid out, as opposed to one hidden
   * with `display: none`.
   *
   * 🔴 This is what keeps the release margin meaning "scrolled far away" (RA-2706). An
   * IntersectionObserver reports a `display: none` element as non-intersecting no matter how large
   * `rootMargin` is, and this feature hides content that way in two routine places: the Top-Down
   * accordion body (`.claim-body { display: none }`) and the status filter's `[hidden]` on pair cards
   * and evidence slides. Without this check, collapsing a finding or flipping the filter would revoke
   * every blob inside it and flash the loading placeholder on the way back — an interaction one click
   * away, not the six-viewport scroll the 6000px margin was tuned for. A hidden element has a
   * zero-area bounding rect, which separates the two cases exactly.
   *
   * @param {IntersectionObserverEntry} entry the observer entry to test
   * @returns {boolean} true when the element still occupies space in the layout
   */
  private isLaidOut(entry: IntersectionObserverEntry): boolean {
    return entry.boundingClientRect.width > 0 || entry.boundingClientRect.height > 0;
  }

  /**
   * Revokes the current object URL (if any) and clears the reference so it cannot be revoked twice.
   */
  private revoke(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

}
